import type { VectorEdge, VectorNetwork, VectorRegion } from "@/document/model/vectorNetwork";
import { vectorRegionGeometries } from "@/document/operations/vectorRegions";
import { createId } from "@/lib/ids";

export interface SplitVectorNetworkPart {
  kind: "closed" | "open";
  network: VectorNetwork;
  sourceEdgeIds: string[];
}

interface EdgeRef {
  edgeId: string;
  forward: boolean;
}

interface SplitVectorNetworkOptions {
  createId?: (prefix: string) => string;
}

/**
 * Decompose a vector network into independent bounded faces and residual open
 * chains. Shared face boundaries are duplicated so every resulting network is
 * a self-contained object.
 */
export function splitVectorNetworkToRegions(
  network: VectorNetwork,
  options: SplitVectorNetworkOptions = {},
): SplitVectorNetworkPart[] {
  const makeId = options.createId ?? createId;
  const parts: SplitVectorNetworkPart[] = [];
  const faceEdgeIds = new Set<string>();

  for (const geometry of vectorRegionGeometries(network)) {
    const refs = geometry.edgeRefs.filter(ref => network.edges.some(edge => edge.id === ref.edgeId));
    if (refs.length === 0) {
      continue;
    }
    refs.forEach(ref => faceEdgeIds.add(ref.edgeId));
    parts.push({
      kind: "closed",
      network: clonePartNetwork(network, refs, makeId, geometry.region),
      sourceEdgeIds: refs.map(ref => ref.edgeId),
    });
  }

  const residualEdges = network.edges.filter(edge => !faceEdgeIds.has(edge.id));
  for (const refs of residualEdgeChains(residualEdges)) {
    const closed = refs.length > 1 && chainStartVertexId(residualEdges, refs) === chainEndVertexId(residualEdges, refs);
    parts.push({
      kind: closed ? "closed" : "open",
      network: clonePartNetwork(network, refs, makeId, closed ? { id: "", edgeIds: refs.map(ref => ref.edgeId) } : undefined),
      sourceEdgeIds: refs.map(ref => ref.edgeId),
    });
  }

  return parts;
}

function clonePartNetwork(
  source: VectorNetwork,
  refs: EdgeRef[],
  makeId: (prefix: string) => string,
  sourceRegion?: VectorRegion,
): VectorNetwork {
  const sourceEdges = refs.flatMap(ref => source.edges.find(edge => edge.id === ref.edgeId) ?? []);
  const sourceVertexIds = new Set(sourceEdges.flatMap(edge => [edge.from.vertexId, edge.to.vertexId]));
  const vertexIds = new Map<string, string>();
  const vertices = source.vertices.flatMap(vertex => {
    if (!sourceVertexIds.has(vertex.id)) {
      return [];
    }
    const id = makeId("vertex");
    vertexIds.set(vertex.id, id);
    return [{ ...structuredClone(vertex), id }];
  });

  const edgeIds = new Map<string, string>();
  const edges = sourceEdges.flatMap(edge => {
    const fromVertexId = vertexIds.get(edge.from.vertexId);
    const toVertexId = vertexIds.get(edge.to.vertexId);
    if (!fromVertexId || !toVertexId) {
      return [];
    }
    const id = makeId("edge");
    edgeIds.set(edge.id, id);
    return [{
      ...structuredClone(edge),
      id,
      from: { ...structuredClone(edge.from), vertexId: fromVertexId },
      to: { ...structuredClone(edge.to), vertexId: toVertexId },
    }];
  });

  const remappedRegionEdgeIds = sourceRegion
    ? refs.flatMap(ref => edgeIds.get(ref.edgeId) ?? [])
    : [];
  const regions: VectorRegion[] = sourceRegion && remappedRegionEdgeIds.length === refs.length
    ? [{
        ...structuredClone(sourceRegion),
        id: makeId("region"),
        edgeIds: remappedRegionEdgeIds,
      }]
    : [];

  const sourceRegionKey = refs.map(ref => ref.edgeId).join(":");
  const nextRegionKey = remappedRegionEdgeIds.join(":");
  const fillOverride = source.fillOverrideTable?.[sourceRegionKey];

  return {
    vertices,
    edges,
    regions,
    fillRule: source.fillRule,
    fillOverrideTable: fillOverride && nextRegionKey
      ? { [nextRegionKey]: structuredClone(fillOverride) }
      : undefined,
  };
}

function residualEdgeChains(edges: VectorEdge[]): EdgeRef[][] {
  const incident = new Map<string, VectorEdge[]>();
  for (const edge of edges) {
    incident.set(edge.from.vertexId, [...(incident.get(edge.from.vertexId) ?? []), edge]);
    incident.set(edge.to.vertexId, [...(incident.get(edge.to.vertexId) ?? []), edge]);
  }

  const used = new Set<string>();
  const starts = edges
    .filter(edge =>
      (incident.get(edge.from.vertexId)?.length ?? 0) !== 2
      || (incident.get(edge.to.vertexId)?.length ?? 0) !== 2,
    )
    .concat(edges);
  const chains: EdgeRef[][] = [];

  for (const first of starts) {
    if (used.has(first.id)) {
      continue;
    }
    const fromDegree = incident.get(first.from.vertexId)?.length ?? 0;
    const toDegree = incident.get(first.to.vertexId)?.length ?? 0;
    const startVertexId = fromDegree !== 2 ? first.from.vertexId : toDegree !== 2 ? first.to.vertexId : first.from.vertexId;
    const chain: EdgeRef[] = [];
    let edge: VectorEdge | undefined = first;
    let fromVertexId = startVertexId;

    while (edge && !used.has(edge.id)) {
      const forward: boolean = edge.from.vertexId === fromVertexId;
      const toVertexId: string = forward ? edge.to.vertexId : edge.from.vertexId;
      chain.push({ edgeId: edge.id, forward });
      used.add(edge.id);

      if ((incident.get(toVertexId)?.length ?? 0) !== 2) {
        break;
      }
      const next: VectorEdge | undefined = (incident.get(toVertexId) ?? []).find(candidate => !used.has(candidate.id));
      fromVertexId = toVertexId;
      edge = next;
    }

    if (chain.length > 0) {
      chains.push(chain);
    }
  }

  return chains;
}

function chainStartVertexId(edges: VectorEdge[], refs: EdgeRef[]): string | undefined {
  const first = refs[0];
  const edge = first ? edges.find(candidate => candidate.id === first.edgeId) : undefined;
  return edge ? (first!.forward ? edge.from.vertexId : edge.to.vertexId) : undefined;
}

function chainEndVertexId(edges: VectorEdge[], refs: EdgeRef[]): string | undefined {
  const last = refs.at(-1);
  const edge = last ? edges.find(candidate => candidate.id === last.edgeId) : undefined;
  return edge ? (last!.forward ? edge.to.vertexId : edge.from.vertexId) : undefined;
}
